import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { call } from "../../../firebase";
import { getDeckCard, listDeckCards, updateDeckCard, updateDeckCardMedia } from "../firestore";
import {
  deleteStoragePath,
  uploadCardAudio,
  uploadCardImage,
  uploadGeneratedCardAudioB64,
  uploadGeneratedCardImageB64,
} from "../storage";

function splitList(value) {
  return String(value || "")
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function CardDetailPage() {
  const { deckId, cardId } = useParams();
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyMedia, setBusyMedia] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [acceptedText, setAcceptedText] = useState("");
  const [strict, setStrict] = useState(false);

  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [audioText, setAudioText] = useState("");
  const [audioInstruction, setAudioInstruction] = useState("");
  const [audioVoice, setAudioVoice] = useState("alloy");
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState("");
  const [refineSuccess, setRefineSuccess] = useState("");
  const [deckCardIds, setDeckCardIds] = useState([]);

  const loadCard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDeckCard(cardId);
      if (data.deckId !== deckId) throw new Error("Card does not belong to this deck.");
      const deckCards = await listDeckCards(deckId, 1500);
      setDeckCardIds(deckCards.map((c) => c.id));
      setCard(data);
      setFront(data.front || "");
      setBack(data.back || "");
      setTagsText((data.tags || []).join(", "));
      setAcceptedText((data.accepted || []).join(", "));
      setStrict(!!data.strict);
      setAudioText(String(data.back || data.front || ""));
    } catch (err) {
      setError(err.message || "Failed to load card.");
    } finally {
      setLoading(false);
    }
  }, [cardId, deckId]);

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  const currentIndex = useMemo(
    () => deckCardIds.findIndex((id) => id === cardId),
    [cardId, deckCardIds]
  );
  const prevCardId = currentIndex > 0 ? deckCardIds[currentIndex - 1] : "";
  const nextCardId = currentIndex >= 0 && currentIndex < deckCardIds.length - 1
    ? deckCardIds[currentIndex + 1]
    : "";

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateDeckCard(cardId, {
        front,
        back,
        tags: splitList(tagsText),
        accepted: splitList(acceptedText),
        strict,
      });
      await loadCard();
      setSuccess("Card updated.");
    } catch (err) {
      setError(err.message || "Failed to save card.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    setBusyMedia(true);
    setError("");
    try {
      const uploaded = await uploadCardImage({ deckId, cardId, file });
      await updateDeckCardMedia(cardId, uploaded);
      await loadCard();
    } catch (err) {
      setError(err.message || "Failed to upload image.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleUploadAudio = async (file) => {
    if (!file) return;
    setBusyMedia(true);
    setError("");
    try {
      const uploaded = await uploadCardAudio({ deckId, cardId, file });
      await updateDeckCardMedia(cardId, uploaded);
      await loadCard();
    } catch (err) {
      setError(err.message || "Failed to upload audio.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!card) return;
    setBusyMedia(true);
    setError("");
    try {
      await deleteStoragePath(card.imagePath);
      await updateDeckCardMedia(cardId, { imageUrl: null, imagePath: null });
      await loadCard();
    } catch (err) {
      setError(err.message || "Failed to remove image.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleRemoveAudio = async () => {
    if (!card) return;
    setBusyMedia(true);
    setError("");
    try {
      await deleteStoragePath(card.audioPath);
      await updateDeckCardMedia(cardId, { audioUrl: null, audioPath: null });
      await loadCard();
    } catch (err) {
      setError(err.message || "Failed to remove audio.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleGenerateImage = async () => {
    const instruction = window.prompt("Optional image instruction for GPT (leave blank for default):", "");
    if (instruction === null) return;
    setBusyMedia(true);
    setError("");
    try {
      const res = await call("generateSrsCardImage", { cardId, instruction: instruction.trim() });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated image returned.");
      const uploaded = await uploadGeneratedCardImageB64({ deckId, cardId, b64 });
      await updateDeckCardMedia(cardId, uploaded);
      await loadCard();
    } catch (err) {
      setError(err.message || "Failed to generate image.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!audioText.trim()) {
      setError("Audio text cannot be empty.");
      return;
    }
    setBusyMedia(true);
    setError("");
    try {
      const res = await call("generateSrsCardAudio", {
        cardId,
        text: audioText.trim(),
        instruction: audioInstruction.trim(),
        voice: audioVoice,
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated audio returned.");
      const uploaded = await uploadGeneratedCardAudioB64({ deckId, cardId, b64 });
      await updateDeckCardMedia(cardId, uploaded);
      await loadCard();
      setAudioDialogOpen(false);
    } catch (err) {
      setError(err.message || "Failed to generate audio.");
    } finally {
      setBusyMedia(false);
    }
  };

  const handleRefineCard = async () => {
    if (!card) return;
    if (!refineInstruction.trim()) {
      setRefineError("Enter a refinement instruction first.");
      return;
    }
    setRefining(true);
    setRefineError("");
    setRefineSuccess("");
    try {
      const res = await call("refineSrsCard", {
        cardId,
        instruction: refineInstruction.trim(),
      });
      const updated = res?.data?.card || res?.data;
      if (!updated?.front || !updated?.back) throw new Error("No updated card returned.");
      setFront(updated.front);
      setBack(updated.back);
      setTagsText((updated.tags || []).join(", "));
      setAcceptedText((updated.accepted || []).join(", "));
      setStrict(typeof updated.strict === "boolean" ? updated.strict : false);
      setRefineSuccess("Applied GPT refinement to editor fields. Save when ready.");
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "refineSrsCard is not deployed. Deploy Firebase functions."
        : err.message || "Failed to refine card.";
      setRefineError(msg);
    } finally {
      setRefining(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={1}>
          <Button component={Link} to={`/srs/decks/${deckId}`}>Back to Deck</Button>
          <Button onClick={() => navigate(`/srs/decks/${deckId}/study`)}>Study Deck</Button>
          <Button disabled={!prevCardId} onClick={() => navigate(`/srs/decks/${deckId}/cards/${prevCardId}`)}>
            Prev
          </Button>
          <Button disabled={!nextCardId} onClick={() => navigate(`/srs/decks/${deckId}/cards/${nextCardId}`)}>
            Next
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        {loading ? (
          <Typography>Loading card...</Typography>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h5" fontWeight={800}>Edit Card</Typography>
              <TextField label="Front" value={front} onChange={(e) => setFront(e.target.value)} fullWidth />
              <TextField label="Back" value={back} onChange={(e) => setBack(e.target.value)} fullWidth multiline minRows={3} />
              <TextField label="Tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} helperText="Comma or semicolon separated" fullWidth />
              <TextField label="Accepted answers" value={acceptedText} onChange={(e) => setAcceptedText(e.target.value)} helperText="Comma or semicolon separated" fullWidth />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Strict checking</Typography>
                <Switch checked={strict} onChange={(e) => setStrict(e.target.checked)} />
              </Stack>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Card"}
              </Button>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={700}>Refine this card with GPT</Typography>
                  <TextField
                    label="Instruction"
                    value={refineInstruction}
                    onChange={(e) => setRefineInstruction(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    placeholder="Example: add a formal definition and use LaTeX where appropriate."
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button variant="outlined" onClick={handleRefineCard} disabled={refining}>
                      {refining ? "Updating..." : "Update Card with GPT"}
                    </Button>
                    {refineSuccess ? <Alert severity="success">{refineSuccess}</Alert> : null}
                  </Stack>
                  {refineError ? <Alert severity="error">{refineError}</Alert> : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6">Media</Typography>
                  <Typography variant="caption" color="text.secondary">{card?.imageUrl ? "Image attached" : "No image"}</Typography>
                  {card?.imageUrl ? (
                    <Box component="img" src={card.imageUrl} alt="card visual" sx={{ width: 180, maxWidth: "100%", border: "1px solid #ddd", borderRadius: 1 }} />
                  ) : null}
                  <Typography variant="caption" color="text.secondary">{card?.audioUrl ? "Audio attached" : "No audio"}</Typography>
                  {card?.audioUrl ? <audio controls preload="none" src={card.audioUrl} style={{ width: "100%" }} /> : null}
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button size="small" component="label" variant="outlined" disabled={busyMedia}>
                      Upload Image
                      <input hidden type="file" accept="image/*" onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        handleUploadImage(file);
                      }} />
                    </Button>
                    <Button size="small" component="label" variant="outlined" disabled={busyMedia}>
                      Upload Audio
                      <input hidden type="file" accept="audio/*,.mp3,.m4a,.wav,.ogg" onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        handleUploadAudio(file);
                      }} />
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleGenerateImage} disabled={busyMedia}>GPT Img</Button>
                    <Button size="small" variant="outlined" onClick={() => setAudioDialogOpen(true)} disabled={busyMedia}>GPT Aud</Button>
                    {card?.imageUrl ? <Button size="small" color="error" onClick={handleRemoveImage} disabled={busyMedia}>Remove Img</Button> : null}
                    {card?.audioUrl ? <Button size="small" color="error" onClick={handleRemoveAudio} disabled={busyMedia}>Remove Aud</Button> : null}
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        )}
      </Stack>

      <Dialog open={audioDialogOpen} onClose={() => setAudioDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Generate GPT Audio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Text to synthesize" value={audioText} onChange={(e) => setAudioText(e.target.value)} multiline minRows={3} fullWidth />
            <TextField label="Pronunciation/style (optional)" value={audioInstruction} onChange={(e) => setAudioInstruction(e.target.value)} fullWidth />
            <TextField label="Voice" value={audioVoice} onChange={(e) => setAudioVoice(e.target.value)} helperText="Examples: alloy, verse, nova, echo" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAudioDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGenerateAudio} variant="contained" disabled={busyMedia}>
            {busyMedia ? "Generating..." : "Generate Audio"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
